import {isEmpty, isEqual} from '@shared/helpers/lang'
import {mapValueKeys, reduceValueKeys} from '@shared/helpers/object'

import {DiscountCoupon, DiscountCouponType} from '@web/core/constants'
import {
    OrderSheetCoupon,
    OrderSheetCouponState,
    OrderSheetDiscount,
    OrderSheetDiscountItem,
    OrderSheetImmediateCoupon,
    OrderSheetSelectedCoupons,
} from '@web/slices/orderSheetCoupon/slice'

import {From} from '..'
import {FetchIntegratedDiscountResponse} from '../interfaces'

type Coupon = FetchIntegratedDiscountResponse['coupon']
type CouponView = Coupon['selectedStoreCouponsByMerchantNo'][string]
type ProductCouponMap = Coupon['selectedProductCouponListsByOrderSheetItemId']
type ImmediateCouponMap = Coupon['selectedImmediateDiscountListsByOrderSheetItemId']
type DuplicateCouponMap = Coupon['selectedProductDuplicateCouponsByOrderSheetItemId']
type DiscountItem = Coupon['deliveryFeeDiscountsByDeliveryGroupKey'][string]

export const getProductCouponType = (
    coupon: Pick<CouponView, 'couponProviderType' | 'couponDiscountType'>,
): DiscountCouponType => {
    const isSeller = coupon.couponProviderType === 'SELLER'
    switch (coupon.couponDiscountType) {
        case 'SELLER_IMMEDIATE_DISCOUNT':
            return DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT
        case 'MULTI_PURCHASE_DISCOUNT':
            return DiscountCoupon.MULTI_PURCHASE_DISCOUNT
        case 'PRODUCT_DISCOUNT':
            return isSeller ? DiscountCoupon.SELLER_PRODUCT_DISCOUNT : DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        case 'DUPLICATE_PRODUCT_DISCOUNT':
            return DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
        case 'STORE_DISCOUNT':
            return DiscountCoupon.STORE_DISCOUNT
    }
    return DiscountCoupon.ORDER_DISCOUNT
}

export const convertDeliveryFeeCoupon = (coupon: CouponView): OrderSheetCoupon => {
    return {
        ...coupon,
        exclusiveCouponType: coupon.exclusiveCouponType ?? undefined,
        couponType: DiscountCoupon.DELIVERY_DISCOUNT,
        disabled: false,
    }
}

export const convertProductCouponMap = (coupon: ProductCouponMap): Record<string, OrderSheetCoupon[]> => {
    return mapValueKeys((couponItems) => {
        return couponItems.map((item) => {
            return {
                ...item,
                exclusiveCouponType: item.exclusiveCouponType ?? undefined,
                couponType: getProductCouponType(item),
                disabled: false,
            }
        })
    }, coupon)
}

export const convertImmediateCouponMap = (coupon: ImmediateCouponMap): Record<string, OrderSheetImmediateCoupon[]> => {
    return mapValueKeys((couponItems) => {
        return couponItems.map((item) => {
            return {
                ...item,
                couponType: DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT,
            }
        })
    }, coupon)
}

export const convertStoreCoupon = (coupon: CouponView): OrderSheetCoupon => {
    return {
        ...coupon,
        exclusiveCouponType: coupon.exclusiveCouponType ?? undefined,
        couponType: DiscountCoupon.STORE_DISCOUNT,
        disabled: false,
    }
}

export const convertDuplicateCouponMap = (coupon: DuplicateCouponMap): Record<string, OrderSheetCoupon[]> => {
    return mapValueKeys((couponItem) => {
        if (couponItem) {
            return [
                {
                    ...couponItem,
                    exclusiveCouponType: couponItem.exclusiveCouponType ?? undefined,
                    couponType: getProductCouponType(couponItem),
                    disabled: false,
                },
            ]
        }
        return []
    }, coupon)
}

export const convertDiscountInfo = (discountItem: DiscountItem): OrderSheetDiscountItem => {
    return {
        ...discountItem,
        validityPeriodEndDateTime: discountItem.validityPeriodEndDateTime ?? undefined,
        multiPurchaseMinOrderValue: discountItem.multiPurchaseMinOrderValue ?? undefined,
        multiPurchaseUnitType: discountItem.multiPurchaseUnitType ?? undefined,
        couponType: getProductCouponType({
            couponDiscountType: discountItem.discountType,
            couponProviderType: discountItem.discountProviderType,
        }),
    }
}

export const filterProductCoupon = <S, C>({
    selectedCoupons,
    couponsByOrderSheetItemId,
    someFunction,
}: {
    selectedCoupons: {[OrderSheetItemId: string]: S[]}
    couponsByOrderSheetItemId: {[OrderSheetItemId: string]: C[]}
    someFunction: (coupon: C, selectedCoupon: S) => boolean
}) => {
    return mapValueKeys((coupons, orderSheetItemId) => {
        return coupons.filter((selectedCoupon) => {
            return couponsByOrderSheetItemId[orderSheetItemId]?.some((coupon) => someFunction(coupon, selectedCoupon))
        })
    }, selectedCoupons)
}

export const getDeliveryFeeCoupons = (coupon: Coupon): OrderSheetCouponState['deliveryFee'] => {
    const couponsByDeliveryGroupKey = mapValueKeys(
        (couponItems) => couponItems.map(convertDeliveryFeeCoupon),
        coupon.deliveryFeeCoupons.deliveryFeeCouponListsByDeliveryGroupKey,
    )
    const usableCount = reduceValueKeys((acc, coupons) => acc + coupons.length, 0, couponsByDeliveryGroupKey)

    return {
        couponsByDeliveryGroupKey,
        usableCount,
        validCount: coupon.deliveryFeeCoupons.validDeliveryFeeCouponCount,
        isOptimized: coupon.discountOptimization,
    }
}

export const getProductCoupons = (coupon: Coupon): OrderSheetCouponState['product'] => {
    return {
        couponsByOrderSheetItemId: convertProductCouponMap(coupon.productCoupons.productCouponListsByOrderSheetItemId),
        duplicateCouponsByOrderSheetItemId: convertProductCouponMap(
            coupon.productCoupons.productDuplicateCouponListsByOrderSheetItemId,
        ),
        immediateCouponsByOrderSheetItemId: convertImmediateCouponMap(
            coupon.productCoupons.immediateDiscountListsByOrderSheetItemId,
        ),
        couponsByMerchantNo: mapValueKeys(
            (couponItems) => couponItems.map(convertStoreCoupon),
            coupon.storeCoupons.storeCouponListsByMerchantNo,
        ),
        multiPurchaseDiscounts: coupon.productCoupons.multiPurchaseDiscountsByOrderSheetItemId ?? {},
        usableCount: coupon.usableProductAndStoreDiscountCount,
        validCount: coupon.validProductAndStoreCouponCount,
    }
}

export const getOptimizedCoupon = (coupon: Coupon): OrderSheetSelectedCoupons => {
    return {
        deliveryCoupon: mapValueKeys(
            (couponItem) => convertDeliveryFeeCoupon(couponItem),
            coupon.selectedDeliveryFeeCouponsByDeliveryGroupKey ?? {},
        ),
        productCoupon: convertProductCouponMap(coupon.selectedProductCouponListsByOrderSheetItemId),
        duplicateCoupon: convertDuplicateCouponMap(coupon.selectedProductDuplicateCouponsByOrderSheetItemId),
        immediateCoupon: convertImmediateCouponMap(coupon.selectedImmediateDiscountListsByOrderSheetItemId),
        storeCoupon: mapValueKeys(convertStoreCoupon, coupon.selectedStoreCouponsByMerchantNo),
        multiPurchaseCoupon: coupon.multiPurchaseDiscountSelectedOrderSheetItemIds ?? [],
    }
}

export const getDiscountInfo = (coupon: Coupon): OrderSheetDiscount => {
    return {
        productItem: mapValueKeys((discounts) => discounts.map(convertDiscountInfo), coupon.discountsByItemId),
        deliveryItem: mapValueKeys(
            (discount) => convertDiscountInfo(discount),
            coupon.deliveryFeeDiscountsByDeliveryGroupKey ?? {},
        ),
    }
}

/** selected(선택된쿠폰)가 coupons(쿠폰리스트)에 포함되어있는지 여부 */
export const validateCoupon = <C>({
    selected,
    coupons,
}: {
    selected: Record<string, Maybe<C>>
    coupons: Record<string, C[]>
}) => {
    return Object.entries(selected).every(([key, selectedCoupon]) => {
        if (!selectedCoupon) {
            return true
        }
        return coupons[key]?.some((coupon) => isEqual(coupon, selectedCoupon))
    })
}

/** selected(선택된쿠폰들)가 coupons(쿠폰리스트)에 포함되어있는지 여부 */
export const validateCouponList = <C>({
    selected,
    coupons,
}: {
    selected: Record<string, C[]>
    coupons: Record<string, C[]>
}) => {
    return Object.entries(selected).every(([key, selectedCoupons]) => {
        return selectedCoupons.every((selectedCoupon) => {
            if (!selectedCoupon) {
                return true
            }
            return coupons[key]?.some((coupon) => isEqual(coupon, selectedCoupon))
        })
    })
}

/**
 * 선택된 쿠폰이 쿠폰리스트에 없는 경우 체크
 */
export const validateSelectedCoupon = ({
    product,
    deliveryFee,
    selected,
}: {
    product: OrderSheetCouponState['product']
    deliveryFee: OrderSheetCouponState['deliveryFee']
    selected: OrderSheetSelectedCoupons
}): {
    isValid: boolean
    selected: OrderSheetSelectedCoupons
} => {
    const {couponsByDeliveryGroupKey} = deliveryFee
    const {
        immediateCouponsByOrderSheetItemId,
        duplicateCouponsByOrderSheetItemId,
        couponsByOrderSheetItemId,
        couponsByMerchantNo,
    } = product

    const isValidDelivery = validateCoupon({selected: selected.deliveryCoupon, coupons: couponsByDeliveryGroupKey})
    const isValidProduct = validateCouponList({selected: selected.productCoupon, coupons: couponsByOrderSheetItemId})
    const isValidDuplicate = validateCouponList({
        selected: selected.duplicateCoupon,
        coupons: duplicateCouponsByOrderSheetItemId,
    })
    const isValidImmediate = validateCouponList({
        selected: selected.immediateCoupon,
        coupons: immediateCouponsByOrderSheetItemId,
    })
    const isValidStore = validateCoupon({selected: selected.storeCoupon, coupons: couponsByMerchantNo})
    const isValidMultiPurchase = !isEmpty(selected.multiPurchaseCoupon)
    return {
        isValid: isValidDelivery && isValidProduct && isValidDuplicate && isValidImmediate && isValidStore,
        selected: {
            deliveryCoupon: isValidDelivery ? selected.deliveryCoupon : {},
            productCoupon: isValidProduct ? selected.productCoupon : {},
            duplicateCoupon: isValidDuplicate ? selected.duplicateCoupon : {},
            immediateCoupon: isValidImmediate ? selected.immediateCoupon : {},
            storeCoupon: isValidStore ? selected.storeCoupon : {},
            multiPurchaseCoupon: isValidMultiPurchase ? selected.multiPurchaseCoupon : [],
        },
    }
}

export const getCouponListChanged = ({
    prev,
    curr,
}: {
    prev: {
        deliveryFee: OrderSheetCouponState['deliveryFee']
        product: OrderSheetCouponState['product']
    }
    curr: {
        deliveryFee: OrderSheetCouponState['deliveryFee']
        product: OrderSheetCouponState['product']
    }
}) => {
    const filterCoupons = <C>(coupons: Record<string, C[]>): Record<string, C[]> => {
        const filteredCoupons = Object.entries(coupons).filter(([_, value]) => !isEmpty(value))
        return Object.fromEntries(filteredCoupons)
    }

    return !(
        isEqual(
            filterCoupons(prev.deliveryFee.couponsByDeliveryGroupKey),
            filterCoupons(curr.deliveryFee.couponsByDeliveryGroupKey),
        ) &&
        isEqual(
            filterCoupons(prev.product.immediateCouponsByOrderSheetItemId),
            filterCoupons(curr.product.immediateCouponsByOrderSheetItemId),
        ) &&
        isEqual(
            filterCoupons(prev.product.duplicateCouponsByOrderSheetItemId),
            filterCoupons(curr.product.duplicateCouponsByOrderSheetItemId),
        ) &&
        isEqual(
            filterCoupons(prev.product.couponsByOrderSheetItemId),
            filterCoupons(curr.product.couponsByOrderSheetItemId),
        ) &&
        isEqual(filterCoupons(prev.product.couponsByMerchantNo), filterCoupons(curr.product.couponsByMerchantNo))
    )
}

export const getCouponPageType = ({
    isBenefitCouponInquiryFailed,
    isSelectedCouponDeleted,
    isCouponListChanged,
    isMyOptimalDiscountChanged,
}: {
    // 페이혜택쿠폰 조회 실패 여부
    isBenefitCouponInquiryFailed: boolean
    // 선택된 쿠폰 제거되었는지 여부
    isSelectedCouponDeleted: boolean
    // 쿠폰리스트 변경 여부
    isCouponListChanged: boolean
    // 나의할인가 변경 여부
    isMyOptimalDiscountChanged: boolean
}): OrderSheetCouponState['couponPageType'] => {
    if (isBenefitCouponInquiryFailed) {
        return 'default'
    }
    if (isSelectedCouponDeleted) {
        return 'blocked'
    }
    if (isCouponListChanged) {
        return 'updated'
    }
    if (isMyOptimalDiscountChanged) {
        return 'reload'
    }
    return 'default'
}

export const getReloadStatus = ({
    from,
    reloadStatus,
}: {
    from?: From
    reloadStatus: OrderSheetCouponState['reloadStatus']
}): OrderSheetCouponState['reloadStatus'] => {
    if (from === 'COUPON_RELOAD' || from === 'DELIVERY_RELOAD') {
        return 'DISABLE'
    }
    if (reloadStatus === 'AVAILABLE') {
        return 'BLOCKED'
    }
    return 'AVAILABLE'
}
